import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import BannerBackground from '@/components/common/BannerBackground';
import logoDark from '@/assets/images/logo-dark.png';
import { apiService } from '@/services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e) => {
    setEmail(e.target.value);

    if (validationError) {
      setValidationError('');
    }

    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = () => {
    if (!email.trim()) {
      setValidationError('Email address is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      // KAN-182: route through apiService (env base URL) instead of a hardcoded fetch.
      const data = await apiService.post('/storefront/forgot-password', {
        email: email.trim()
      });

      if (data.status === 'Success' || data.Status === 'Success') {
        setSuccessMessage(data.message || data.Message || 'If an account exists with that email, a reset link has been sent.');
        setIsSuccess(true);
      } else {
        setSubmitError(data.message || data.Message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setSubmitError(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-vh-100 d-flex align-items-center bg-light">
        <BannerBackground/>
        <Container className="position-relative">
          <Row className="justify-content-center">
            <Col md={6} lg={5} xl={4}>
              <Card className="shadow border-0">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <img 
                        src={logoDark}
                        alt="Stash House"
                        className="stash-header__logo-img img-fluid mb-3"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'inline';
                        }}
                    />
                  </div>

                  <div className="text-center mb-4">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <svg 
                        width="32" 
                        height="32" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-success"
                      >
                        <path 
                          d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6M22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6M22 6L12 13L2 6" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <h4 className="fw-bold text-success mb-2">Check Your Email</h4>
                  </div>

                  <Alert variant="success" className="mb-4">
                    {successMessage}
                  </Alert>

                  <p className="text-muted text-center mb-4">
                    If you don't see the email in your inbox, please check your spam folder.
                  </p>

                  <div className="d-grid gap-2">
                    <Link to="/login" className="btn btn-primary btn-lg">
                      Back to Login
                    </Link>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => {
                        setIsSuccess(false);
                        setEmail('');
                      }}
                    >
                      Send Another Email
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <BannerBackground/>
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            <Card className="shadow border-0">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <img 
                      src={logoDark}
                      alt="Stash House"
                      className="stash-header__logo-img img-fluid mb-3"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'inline';
                      }}
                  />
                  <p className="text-muted">Forgot Your Password?</p>
                </div>

                <p className="text-muted text-center mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                {submitError && (
                  <Alert variant="danger" className="mb-4" dismissible onClose={() => setSubmitError('')}>
                    {submitError}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={handleInputChange}
                      isInvalid={!!validationError}
                      placeholder="Enter your email address"
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationError}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-grid gap-2 mb-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <Link to="/login" className="btn btn-outline-secondary btn-lg">
                      Back to Login
                    </Link>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            <div className="text-center mt-4">
              <small className="text-muted">
                Remember your password?{' '}
                <Link to="/login" className="text-decoration-none">
                  Sign in here
                </Link>
              </small>
            </div>

            <div className="text-center mt-3">
              <small className="text-muted">
                 Need help? Contact your sales representative or email us at <a href="mailto:info@stashhousedistro.com">info@stashhousedistro.com</a>
                {/* or{' '}
                <Link to="/support" className="text-decoration-none">
                  visit our support page
                </Link> */}
              </small>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgotPasswordPage;